/**
 * Appends blog-attribution UTM params to an outbound product link.
 *
 * Used at the component level (header/footer/CTA links) to close
 * blog -> signup attribution. Never applied inside markdown post content.
 */

export interface WithUtmOptions {
	/** Stable per-component campaign identifier, e.g. 'header-nav' */
	campaign: string
	/** Extra context, e.g. the post slug — omitted when not in scope */
	content?: string
}

const RELATIVE_BASE = 'https://relative.invalid'

export function withUtm(url: string, opts: WithUtmOptions): string {
	// Protocol-relative URLs (`//host/path`) have no scheme, which `new URL`
	// refuses to parse standalone — borrow one and strip it back off below.
	const isProtocolRelative = url.startsWith('//')
	const isRelative = !/^[a-z][a-z0-9+.-]*:\/\//i.test(url) && !isProtocolRelative

	const parsed = new URL(isProtocolRelative ? `https:${url}` : url, isRelative ? RELATIVE_BASE : undefined)

	parsed.searchParams.set('utm_source', 'blog')
	parsed.searchParams.set('utm_medium', 'referral')
	parsed.searchParams.set('utm_campaign', opts.campaign)
	if (opts.content) parsed.searchParams.set('utm_content', opts.content)

	if (isProtocolRelative) return parsed.toString().slice('https:'.length)
	if (!isRelative) return parsed.toString()

	// Strip the fake base back off so relative input stays relative.
	return `${parsed.pathname}${parsed.search}${parsed.hash}`
}
